import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Grid,
  Stack,
  IconButton
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Router as RouterIcon,
  Launch as LaunchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import alertsApiService from '../../services/alerts/AlertsApiService';

const Overview = () => {
  const [agents, setAgents] = useState([]);
  const [remoteConnections, setRemoteConnections] = useState([]);
  const [statistics, setStatistics] = useState({
    totalAgents: 0,
    activeAgents: 0,
    inactiveAgents: 0,
    securityAlerts: 0,
    remoteConnections: 0
  });
  const [pieChartData, setPieChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchRemoteConnections = async () => {
    try {
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const user = JSON.parse(localStorage.getItem('user_info') || '{}');
      
      // Add authentication header if available
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('cognito_token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/remote-connections/user-connections`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ username: user.username || 'demo' }),
      });

      if (!response.ok) {
        console.warn('Remote connections API returned:', response.status, response.statusText);
        return 0;
      }

      const data = await response.json();
      const connections = data.tree?.childConnections 
        ? Object.values(data.tree.childConnections) 
        : [];
      setRemoteConnections(connections);
      return connections.length;
    } catch (err) {
      console.warn('Failed to fetch remote connections:', err);
      // Don't show error to user, just return 0 connections
      return 0;
    }
  };

  const fetchDashboardData = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if token is available, if not, wait a bit and retry
      const token = localStorage.getItem('cognito_token');
      if (!token && retryCount < 3) {
        console.log('No token available, waiting and retrying...');
        setRetryCount(retryCount + 1);
        setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
        return;
      }
      
      const [agentsData, alertsData, remoteConnectionsCount] = await Promise.all([
        dashboardApiService.getAllAgents(),
        alertsApiService.getAllAlerts(),
        fetchRemoteConnections()
      ]);
      
      const stats = dashboardApiService.getAgentStatistics(agentsData);
      const chartData = dashboardApiService.getPieChartData(agentsData);
      
      setAgents(agentsData);
      setStatistics({
        ...stats,
        securityAlerts: alertsData.count || 0,
        remoteConnections: remoteConnectionsCount
      });
      setPieChartData(chartData);
    } catch (err) {
      // If error is about missing token and we haven't retried much, try again
      if (err.message && err.message.includes('No authentication token available') && retryCount < 3) {
        console.log('Token error, retrying...', err.message);
        setRetryCount(retryCount + 1);
        setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
        return;
      }
      
      setError('Failed to fetch dashboard data');
      console.error('Dashboard data fetch error:', err);
      setRetryCount(0);
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      } else {
        // Reset retry count after successful load or final failure
        setRetryCount(0);
        setLoading(false);
      }
    }
  };


  const handleAgentSelect = (event) => {
    const agentId = event.target.value;
    setSelectedAgent(agentId);
    if (agentId !== 'all') {
      navigate(`/agents/${agentId}`);
    }
  };

  const formatLastKeepAlive = (lastKeepAlive) => {
    if (lastKeepAlive === '9999-12-31T23:59:59+00:00') {
      return 'Never disconnected';
    }
    return new Date(lastKeepAlive).toLocaleString();
  };

  const handleOverviewCardClick = (cardType) => {
    // Navigate to appropriate page based on card type
    switch (cardType) {
      case 'totalAgents':
        navigate('/agents?filter=all');
        break;
      case 'activeAgents':
        navigate('/agents?filter=active');
        break;
      case 'inactiveAgents':
        navigate('/agents?filter=inactive');
        break;
      case 'securityAlerts':
        navigate('/alerts');
        break;
      case 'remoteConnections':
        navigate('/remote-connections');
        break;
      default:
        navigate('/agents');
    }
  };

  const handleConnectToRemote = (connection) => {
    // This would open the remote connection - similar to RemoteConnections component
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const user = JSON.parse(localStorage.getItem('user_info') || '{}');
    
    // For now, just navigate to remote connections page
    navigate('/remote-connections');
  };


  if (loading) {
    return (
      <Box className="overview-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">
          {retryCount > 0 ? `Checking authentication... (${retryCount}/3)` : 'Loading dashboard...'}
        </Typography>
        {retryCount > 0 && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Waiting for authentication tokens...
          </Typography>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="overview-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchDashboardData}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="overview">
      <Box className="overview-header">
        <Typography variant="h4" className="overview-title">
          Security Overview
        </Typography>
        <Box className="overview-actions" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={handleAgentSelect}
              label="Select Agent"
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    All Agents Overview
                  </Typography>
                </Box>
              </MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                      <ComputerIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {agent.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {agent.id} • {agent.ip}
                      </Typography>
                    </Box>
                    <Chip
                      label={agent.status === 'active' ? 'Active' : 'Inactive'}
                      size="small"
                      color={agent.status === 'active' ? 'success' : 'error'}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="contained" 
            onClick={fetchDashboardData}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>


      {/* Statistics Cards */}
      <Box className="stats-grid">
        <Card 
          className="stat-card total-agents"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('totalAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <PeopleIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Box className="stat-number">{statistics.totalAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Total Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card active-agents"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('activeAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
              <Box className="stat-number">{statistics.activeAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Active Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card security-alerts"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('securityAlerts')}
        >
          <CardContent>
            <Box className="stat-content">
              <WarningIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1 }} />
              <Box className="stat-number">{statistics.securityAlerts}</Box>
              <Typography variant="body2" className="stat-label">
                Security Alerts
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card disconnected"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('inactiveAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <WarningIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
              <Box className="stat-number">{statistics.inactiveAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Inactive Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card remote-connections"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('remoteConnections')}
        >
          <CardContent>
            <Box className="stat-content">
              <RouterIcon sx={{ fontSize: 40, color: '#7c3aed', mb: 1 }} />
              <Box className="stat-number">{statistics.remoteConnections}</Box>
              <Typography variant="body2" className="stat-label">
                Remote Connections
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Remote Connections Quick View */}
      <Box className="remote-connections-section" sx={{ mt: 3 }}>
        <Card sx={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Quick Remote Access
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => navigate('/remote-connections')}
                sx={{ borderRadius: 2 }}
              >
                Manage All
              </Button>
            </Box>
            
            {remoteConnections.length > 0 ? (
              <Grid container spacing={2}>
                {remoteConnections.slice(0, 4).map((connection) => (
                  <Grid item xs={12} sm={6} md={3} key={connection.identifier}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }
                      }}
                      onClick={() => handleConnectToRemote(connection)}
                    >
                      <CardContent sx={{ p: 2, textAlign: 'center' }}>
                        <Avatar sx={{ 
                          bgcolor: connection.protocol?.toLowerCase() === 'rdp' ? 'primary.main' : 'success.main',
                          width: 48, 
                          height: 48, 
                          mx: 'auto', 
                          mb: 1 
                        }}>
                          <RouterIcon />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {connection.name}
                        </Typography>
                        <Chip
                          label={connection.protocol?.toUpperCase() || 'Unknown'}
                          size="small"
                          color={connection.protocol?.toLowerCase() === 'rdp' ? 'primary' : 'success'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <RouterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No remote connections configured
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/remote-connections')}
                  sx={{ borderRadius: 2 }}
                >
                  Add Your First Connection
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>


      {/* Charts Section */}
      <Box className="charts-section">
        <Card className="chart-card agent-status-chart">
          <CardContent>
            <Typography variant="h6" className="chart-title">
              Agent Status Distribution
            </Typography>
            <Box className="pie-chart-container">
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={3}
                      dataKey="value"
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={1000}
                      style={{ cursor: 'default' }}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.fill}
                          style={{ cursor: 'default' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      className="pie-chart-tooltip"
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={40}
                      iconType="circle"
                      className="pie-chart-legend"
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box className="chart-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card className="chart-card recent-events">
          <CardContent>
            <Typography variant="h6" className="chart-title">
              Recent Security Events
            </Typography>
            <Box className="events-list">
              {agents.slice(0, 5).map((agent, index) => (
                <Box key={agent.id} className="event-item">
                  <Typography variant="body2" className="event-text">
                    {agent.status === 'active' ? 'Agent connected' : 'Agent disconnected'}: {agent.name}
                  </Typography>
                  <Typography variant="caption" className="event-time">
                    {formatLastKeepAlive(agent.lastKeepAlive)}
                  </Typography>
                </Box>
              ))}
              {agents.length === 0 && (
                <Box className="event-item">
                  <Typography variant="body2" className="event-text">
                    No recent events
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Overview;
